/**
 * The children a device or server knows about before anyone captures a card.
 *
 * A production install is issued `EMPTY_DATASET`: no registry rows, no household
 * list, no records. Those arrive from the real sources — the e-Tracker, the Union
 * Parishad list — or from the worker's own captures. The fabricated dataset in
 * fixtures/demo.ts has the same shape and exists for the automated suites.
 */
import { SITE, type Household, type RegistryChild, type VvmSeedReview, type ZeroDoseCaseSeed } from './reference-data.js';

export interface Dataset {
  name: 'empty' | 'demonstration';
  /** A fixed clinical date, so fixtures read the same whenever they run. Null: the real date. */
  referenceDate: string | null;
  /** A fixed session, for the same reason. Null: one session per day at the site. */
  session: { id: string; startedAt: string } | null;
  registry: RegistryChild[];
  registryRefreshedOn: string | null;
  households: Household[];
  householdListRef: string | null;
  zeroDoseCases: ZeroDoseCaseSeed[];
  priorPilotRegistrations: number;
  vvmReviews: VvmSeedReview[];
  /** Records already synced before the session opened. */
  seedRecords: ((confirmedBy: string) => {
    clientId: string; sessionId: string; etrackerRef: string; capturedAt: string; confirmedAt: string;
    confirmedBy: string; offlineCapture: boolean; captureMs: number; activeEntryMs: number; imageRef: string;
    newEnrolment: boolean;
    fields: { key: string; label: string; labelBn: string; value: string; raw: string; confidence: number; resolved: boolean; edited: boolean }[];
    doses: { antigen: string; givenOn: string; raw: string; confidence: number; source: 'card' | 'session' }[];
    resolutions: { flagCode: string; choice: string; kind: 'correction' | 'accept-invalid'; note: string | null; at: string }[];
    events: { at: string; title: string; detail: string | null }[];
  }[]) | null;
}

export const EMPTY_DATASET: Dataset = {
  name: 'empty',
  referenceDate: null,
  session: null,
  registry: [],
  registryRefreshedOn: null,
  households: [],
  householdListRef: null,
  zeroDoseCases: [],
  priorPilotRegistrations: 0,
  vvmReviews: [],
  seedRecords: null
};

/** Today's date in Bangladesh, whatever time zone the device or server is set to. */
export function dhakaToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 6 * 3_600_000).toISOString().slice(0, 10);
}

/** The clinical "today" under a dataset. */
export const clinicalToday = (d: Dataset, now?: Date): string => d.referenceDate ?? dhakaToday(now);

/** A day's session at the site: `SES-2026-09-26`. */
export const sessionIdFor = (date: string) => `SES-${date}`;

/** Whether an id names a daily session, and the date it names. */
export function sessionDate(id: string): string | null {
  const m = /^SES-(\d{4}-\d{2}-\d{2})$/.exec(id);
  return m ? m[1]! : null;
}

export interface SessionRow {
  id: string; heldOn: string; site: string; siteBn: string; union: string; upazila: string;
  district: string; orgUnit: string; planned: number; startedAt: string;
}

/** The session open on a date. */
export function sessionFor(d: Dataset, date: string = clinicalToday(d), startedAt?: string): SessionRow {
  return {
    ...SITE,
    id: d.session?.id ?? sessionIdFor(date),
    heldOn: d.referenceDate ?? date,
    startedAt: d.session?.startedAt ?? startedAt ?? `${date}T09:00:00+06:00`
  };
}

/**
 * Identifiers the demonstration dataset installed on devices that ran an earlier
 * build. A device that still holds them has them removed at its next start.
 */
export const LEGACY_DEMO_IDS = {
  recordPrefix: 'seed-',
  vvm: ['VVM-0091', 'VVM-0092', 'VVM-0093', 'VVM-0094', 'VVM-0095', 'VVM-0096', 'VVM-0097', 'VVM-0098', 'VVM-0099', 'VVM-0100'],
  zeroDoseCases: ['C-8860', 'C-8840']
};
